

import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  FiLogOut,
  FiPackage,
  FiCreditCard,
  FiClock,
  FiXCircle,
  FiEdit3,
  FiTrash2,
  FiLoader,
  FiChevronLeft,
  FiChevronRight
} from 'react-icons/fi'
import axios from 'axios'
import toast from 'react-hot-toast'
import { formatCurrencyInput, unformatCurrencyInput } from '../../utils/formatters'

export default function UserAdminDashboard() {
  const navigate = useNavigate()
  const [stats, setStats] = useState({})
  const [items, setItems] = useState([])
  const [totalItems, setTotalItems] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [limit] = useState(10) // items per page
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [editingItem, setEditingItem] = useState(null)
  const [editData, setEditData] = useState({})
  const [updatingItem, setUpdatingItem] = useState(false)
  const [deletingItemId, setDeletingItemId] = useState(null)

  useEffect(() => {
    fetchDashboardData()
  }, [])

  useEffect(() => {
    fetchItemsPage(currentPage)
  }, [currentPage])

  const fetchDashboardData = async () => {
    try {
      const dashboardResponse = await axios.get('/admin/dashboard')
      setStats(dashboardResponse.data.stats)
    } catch (error) {
      toast.error('Error loading dashboard data')
    }
  }

  const fetchItemsPage = async (page = 1) => {
    setLoading(true)
    try {
      const response = await axios.get('/admin/items', {
        params: { page, limit }
      })
      setItems(response.data.items)
      setTotalItems(response.data.total)
      setTotalPages(response.data.totalPages)
    } catch (error) {
      toast.error('Error loading items')
    } finally {
      setLoading(false)
    }
  }

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(prev => prev - 1)
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(prev => prev + 1)
  }

  const handleLogoutAdmin = () => {
    localStorage.removeItem('userAdminToken')
    toast.success('Logged out from admin panel')
    navigate('/dashboard')
  }

  const handleEditItem = (item) => {
    setEditingItem(item._id)
    setEditData({
      name: item.name || '',
      description: item.description || '',
      price: formatCurrencyInput(item.price),
      quantity: item.quantity ? String(item.quantity) : '1',
      status: item.status || '',
      category: item.category || '',
      customerName: item.customerName || ''
    })
  }

  const handleUpdateItem = async (e) => {
    e.preventDefault()
    setUpdatingItem(true)
    try {
      const cleaned = editData.price?.toString().replace(/,/g, '') || ''
      const customerName = editData.customerName?.toString().trim() || ''
      if (!editData.name || !cleaned || !customerName) {
        toast.error('Please fill in required fields')
        setUpdatingItem(false)
        return
      }
      const payload = { ...editData, price: Number(cleaned), customerName }
      await axios.put(`/admin/items/${editingItem}`, payload)
      toast.success('Item updated successfully')
      setEditingItem(null)
      fetchItemsPage(currentPage)
      fetchDashboardData()
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating item')
    } finally {
      setUpdatingItem(false)
    }
  }

  const handleDeleteItem = async (itemId) => {
    if (!confirm('Are you sure you want to delete this item?')) return
    setDeletingItemId(itemId)
    try {
      await axios.delete(`/admin/items/${itemId}`)
      toast.success('Item deleted successfully')
      fetchItemsPage(currentPage)
      fetchDashboardData()
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting item')
    } finally {
      setDeletingItemId(null)
    }
  }

  const getStatusBadge = (status) => {
    const badges = {
      paid: 'bg-success-100 text-success-800',
      unpaid: 'bg-error-100 text-error-800',
      pending: 'bg-warning-100 text-warning-800'
    }
    return badges[status] || 'bg-gray-100 text-gray-800'
  }

  const statCards = [
    {
      title: 'Total Items',
      value: stats.totalItems || 0,
      icon: FiPackage,
      color: 'bg-blue-500',
      textColor: 'text-blue-600'
    },
    {
      title: 'Paid Items',
      value: (stats.paidItems || 0).toLocaleString(),
      icon: FiCreditCard,
      color: 'bg-success-500',
      textColor: 'text-success-600'
    },
    {
      title: 'Unpaid Items',
      value: stats.unpaidItems || 0,
      icon: FiXCircle,
      color: 'bg-error-500',
      textColor: 'text-error-600'
    },
    {
      title: 'Pending Items',
      value: stats.pendingItems || 0,
      icon: FiClock,
      color: 'bg-warning-500',
      textColor: 'text-warning-600'
    }
  ]

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="card h-24 bg-gray-200"></div>
          ))}
        </div>
      </div>
    )
  }

  const startItem = (currentPage - 1) * limit + 1
  const endItem = Math.min(currentPage * limit, totalItems)

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-0">
      {/* Stats Section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
        {statCards.map((card, i) => (
          <div key={i} className={`card flex items-center gap-3 sm:gap-4 p-3 sm:p-4 ${card.color} text-white`}>
            <card.icon className="w-5 sm:w-6 h-5 sm:h-6 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-xs sm:text-sm font-medium">{card.title}</div>
              <div className="text-lg sm:text-xl font-semibold">{card.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Items Management */}
      <div className="card">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Items Management</h3>
          <span className="text-xs sm:text-sm text-gray-500">
            Showing {startItem}-{endItem} of {totalItems} items
          </span>
        </div>

        {items.length > 0 ? (
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                  <th className="px-4 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Price</th>
                  <th className="px-4 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Status</th>
                  <th className="px-4 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Created</th>
                  <th className="px-4 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((item) => (
                  <tr key={item._id} className="hover:bg-gray-50 text-xs sm:text-sm">
                    <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {item.image && (
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg object-cover flex-shrink-0"
                          />
                        )}
                        <div className="min-w-0">
                          <div className="text-xs sm:text-sm font-medium text-gray-900 truncate">{item.name}</div>
                          {item.customerName && <div className="text-xs text-gray-500 truncate hidden sm:block">Customer: {item.customerName}</div>}
                          {item.category && <div className="text-xs text-gray-500 truncate hidden sm:block">{item.category}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap hidden sm:table-cell">
                      <div className="text-xs sm:text-sm font-medium text-gray-900">
                        {item.currency || 'NGN'} {item.price?.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap hidden md:table-cell">
                      <span className={`px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full text-xs font-medium ${getStatusBadge(item.status)}`}>
                        {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 hidden lg:table-cell">
                      {new Date(item.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="flex gap-2">
                        <button onClick={() => handleEditItem(item)} aria-label={`Edit item ${item._id}`} className="text-blue-600 hover:text-blue-900 p-1 sm:p-1.5 hover:bg-blue-50 rounded transition">
                          <FiEdit3 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDeleteItem(item._id)} aria-label={`Delete item ${item._id}`} className="text-red-600 hover:text-red-900 p-1 sm:p-1.5 hover:bg-red-50 rounded transition">
                          {deletingItemId === item._id ? <FiLoader className="animate-spin w-4 h-4" /> : <FiTrash2 className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-6 sm:py-8">
            <FiPackage className="w-10 sm:w-12 h-10 sm:h-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
            <p className="text-xs sm:text-sm text-gray-600">No items to manage</p>
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row justify-end items-center gap-3 sm:gap-3 mt-4">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="w-full sm:w-auto btn btn-secondary flex items-center justify-center space-x-1 py-2 sm:py-2 px-3 sm:px-4 text-sm sm:text-base"
            >
              <FiChevronLeft /> <span>Prev</span>
            </button>
            <span className="text-xs sm:text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="w-full sm:w-auto btn btn-secondary flex items-center justify-center space-x-1 py-2 sm:py-2 px-3 sm:px-4 text-sm sm:text-base"
            >
              <span>Next</span> <FiChevronRight />
            </button>
          </div>
        )}
      </div>

      {/* Edit Item Modal */}
      {editingItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-xs sm:max-w-md w-full max-h-[calc(100vh-2rem)] overflow-y-auto p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-6">Edit Item</h3>
            <form onSubmit={handleUpdateItem} className="space-y-4 sm:space-y-5">

              {/* Customer Name */}
              <div>
                <label htmlFor="customer-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Customer Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="customer-name"
                  value={editData.customerName ?? ''}
                  onChange={e => setEditData(prev => ({ ...prev, customerName: e.target.value }))}
                  className="input-field text-sm sm:text-base py-2 sm:py-3 w-full"
                  placeholder="Enter customer name"
                  required
                  aria-label="Customer name"
                />
                <p className="text-xs text-gray-500 mt-1">The customer name shown on invoices and item records</p>
              </div>
              
              {/* Item Name */}
              <div>
                <label htmlFor="item-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Item Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="item-name"
                  value={editData.name}
                  onChange={e => setEditData(prev => ({ ...prev, name: e.target.value }))}
                  className="input-field text-sm sm:text-base py-2 sm:py-3 w-full"
                  placeholder="Enter item name"
                  required
                  aria-label="Item name"
                />
                <p className="text-xs text-gray-500 mt-1">The name of the item or product</p>
              </div>

              {/* Description */}
              <div>
                <label htmlFor="item-description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="item-description"
                  value={editData.description}
                  onChange={e => setEditData(prev => ({ ...prev, description: e.target.value }))}
                  className="input-field text-sm sm:text-base py-2 sm:py-3 w-full"
                  rows={3}
                  placeholder="Describe the item in detail"
                  aria-label="Item description"
                />
                <p className="text-xs text-gray-500 mt-1">Optional detailed description of the item</p>
              </div>

              {/* Price */}
              <div>
                <label htmlFor="item-price" className="block text-sm font-medium text-gray-700 mb-1">
                  Price <span className="text-red-500">*</span>
                </label>
                <input
                  id="item-price"
                  type="text"
                  value={editData.price ?? ''}
                  onChange={e => {
                    const cleaned = unformatCurrencyInput(e.target.value).replace(/[^\d.]/g, '')
                    setEditData(prev => ({ ...prev, price: cleaned }))
                  }}
                  onFocus={() => setEditData(prev => ({ ...prev, price: unformatCurrencyInput(prev.price) }))}
                  onBlur={() => setEditData(prev => ({ ...prev, price: formatCurrencyInput(prev.price) }))}
                  className="input-field text-sm sm:text-base py-2 sm:py-3 w-full"
                  placeholder="0.00"
                  required
                  aria-label="Item price"
                />
                <p className="text-xs text-gray-500 mt-1">The price of the item in your default currency</p>
              </div>

              {/* Quantity */}
              <div>
                <label htmlFor="item-quantity" className="block text-sm font-medium text-gray-700 mb-1">
                  Quantity
                </label>
                <input
                  id="item-quantity"
                  type="number"
                  min="1"
                  value={editData.quantity ?? '1'}
                  onChange={e => setEditData(prev => ({ ...prev, quantity: e.target.value }))}
                  className="input-field text-sm sm:text-base py-2 sm:py-3 w-full"
                  placeholder="1"
                  aria-label="Item quantity"
                />
                <p className="text-xs text-gray-500 mt-1">How many items are available</p>
              </div>

              {/* Status */}
              <div>
                <label htmlFor="item-status" className="block text-sm font-medium text-gray-700 mb-1">
                  Status <span className="text-red-500">*</span>
                </label>
                <select
                  id="item-status"
                  value={editData.status}
                  onChange={e => setEditData(prev => ({ ...prev, status: e.target.value }))}
                  className="input-field text-sm sm:text-base py-2 sm:py-3 w-full"
                  required
                  aria-label="Item status"
                >
                  <option value="">-- Select Status --</option>
                  <option value="paid">Paid</option>
                  <option value="unpaid">Unpaid</option>
                  <option value="pending">Pending</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">The payment or availability status of this item</p>
              </div>

              {/* Category */}
              <div>
                <label htmlFor="item-category" className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <input
                  id="item-category"
                  value={editData.category}
                  onChange={e => setEditData(prev => ({ ...prev, category: e.target.value }))}
                  className="input-field text-sm sm:text-base py-2 sm:py-3 w-full"
                  placeholder="e.g., Electronics, Clothing, etc."
                  aria-label="Item category"
                />
                <p className="text-xs text-gray-500 mt-1">The category or type of item</p>
              </div>

              {/* Form Actions */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-6 border-t">
                <button 
                  type="button" 
                  onClick={() => setEditingItem(null)} 
                  className="flex-1 btn btn-secondary text-sm sm:text-base py-2 sm:py-3"
                  aria-label="Cancel editing item"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={updatingItem} 
                  className="flex-1 btn btn-primary text-sm sm:text-base py-2 sm:py-3 flex items-center justify-center gap-2"
                  aria-label="Update item with changes"
                >
                  {updatingItem ? (
                    <>
                      <FiLoader className="animate-spin w-4 h-4" />
                      <span>Updating...</span>
                    </>
                  ) : (
                    <span>Update</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}




// import { useState, useEffect } from 'react'
// import { useNavigate } from 'react-router-dom'
// import {
//   FiLogOut,
//   FiPackage,
//   FiCreditCard,
//   FiClock,
//   FiXCircle,
//   FiEdit3,
//   FiTrash2,
//   FiLoader
// } from 'react-icons/fi'
// import axios from 'axios'
// import toast from 'react-hot-toast'

// export default function UserAdminDashboard() {
//   const navigate = useNavigate()
//   const [stats, setStats] = useState({})
//   const [items, setItems] = useState([])
//   const [loading, setLoading] = useState(true)
//   const [editingItem, setEditingItem] = useState(null)
//   const [editData, setEditData] = useState({})
//   const [updatingItem, setUpdatingItem] = useState(false)
//   const [deletingItemId, setDeletingItemId] = useState(null)

//   useEffect(() => {
//     fetchDashboardData()
//   }, [])

//   const fetchDashboardData = async () => {
//     try {
//       const [dashboardResponse, itemsResponse] = await Promise.all([
//         axios.get('/admin/dashboard'),
//         axios.get('/admin/items?limit=20')
//       ])
//       setStats(dashboardResponse.data.stats)
//       setItems(itemsResponse.data.items)
//     } catch (error) {
//       toast.error('Error loading dashboard data')
//     } finally {
//       setLoading(false)
//     }
//   }

//   const handleLogoutAdmin = () => {
//     localStorage.removeItem('userAdminToken')
//     toast.success('Logged out from admin panel')
//     navigate('/dashboard')
//   }

//   const handleEditItem = (item) => {
//     setEditingItem(item._id)
//     setEditData({
//       name: item.name || '',
//       description: item.description || '',
//       price: item.price ?? '',
//       status: item.status || '',
//       category: item.category || ''
//     })
//   }

//   const handleUpdateItem = async (e) => {
//     e.preventDefault()
//     setUpdatingItem(true)
//     try {
//       await axios.put(`/admin/items/${editingItem}`, editData)
//       toast.success('Item updated successfully')
//       setEditingItem(null)
//       fetchDashboardData()
//     } catch (error) {
//       toast.error(error.response?.data?.message || 'Error updating item')
//     } finally {
//       setUpdatingItem(false)
//     }
//   }

//   const handleDeleteItem = async (itemId) => {
//     if (!confirm('Are you sure you want to delete this item?')) return
//     setDeletingItemId(itemId)
//     try {
//       await axios.delete(`/admin/items/${itemId}`)
//       toast.success('Item deleted successfully')
//       fetchDashboardData()
//     } catch (error) {
//       toast.error(error.response?.data?.message || 'Error deleting item')
//     } finally {
//       setDeletingItemId(null)
//     }
//   }

//   const getStatusBadge = (status) => {
//     const badges = {
//       paid: 'bg-success-100 text-success-800',
//       unpaid: 'bg-error-100 text-error-800',
//       pending: 'bg-warning-100 text-warning-800'
//     }
//     return badges[status] || 'bg-gray-100 text-gray-800'
//   }

//   const statCards = [
//     {
//       title: 'Total Items',
//       value: stats.totalItems || 0,
//       icon: FiPackage,
//       color: 'bg-blue-500',
//       textColor: 'text-blue-600'
//     },
//     {
//       title: 'Paid Items',
//       value: (stats.paidItems || 0).toLocaleString(),
//       icon: FiCreditCard,
//       color: 'bg-success-500',
//       textColor: 'text-success-600'
//     },
//     {
//       title: 'Unpaid Items',
//       value: stats.unpaidItems || 0,
//       icon: FiXCircle,
//       color: 'bg-error-500',
//       textColor: 'text-error-600'
//     },
//     {
//       title: 'Pending Items',
//       value: stats.pendingItems || 0,
//       icon: FiClock,
//       color: 'bg-warning-500',
//       textColor: 'text-warning-600'
//     }
//   ]

//   if (loading) {
//     return (
//       <div className="animate-pulse">
//         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
//           {[...Array(4)].map((_, i) => (
//             <div key={i} className="card h-24 bg-gray-200"></div>
//           ))}
//         </div>
//       </div>
//     )
//   }

//   return (
//     <div className="max-w-7xl mx-auto">
//       {/* Stats Section */}
//       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
//         {statCards.map((card, i) => (
//           <div key={i} className={`card flex items-center space-x-4 p-4 ${card.color} text-white`}>
//             <card.icon className="w-6 h-6" />
//             <div>
//               <div className="text-sm">{card.title}</div>
//               <div className="text-lg font-semibold">{card.value}</div>
//             </div>
//           </div>
//         ))}
//       </div>

//       {/* Items Management */}
//       <div className="card">
//         <div className="flex items-center justify-between mb-6">
//           <h3 className="text-lg font-semibold text-gray-900">Items Management</h3>
//           <span className="text-sm text-gray-500">{items.length} items</span>
//         </div>

//         {items.length > 0 ? (
//           <div className="overflow-x-auto">
//             <table className="min-w-full divide-y divide-gray-200">
//               <thead className="bg-gray-50">
//                 <tr>
//                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
//                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
//                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
//                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
//                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
//                 </tr>
//               </thead>
//               <tbody className="bg-white divide-y divide-gray-200">
//                 {items.map((item) => (
//                   <tr key={item._id} className="hover:bg-gray-50">
//                     <td className="px-6 py-4 whitespace-nowrap">
//                       <div className="flex items-center">
//                         {item.image && (
//                           <img
//                             src={item.image}
//                             alt={item.name}
//                             className="w-10 h-10 rounded-lg object-cover mr-3"
//                           />
//                         )}
//                         <div>
//                           <div className="text-sm font-medium text-gray-900">{item.name}</div>
//                           {item.category && <div className="text-sm text-gray-500">{item.category}</div>}
//                         </div>
//                       </div>
//                     </td>
//                     <td className="px-6 py-4 whitespace-nowrap">
//                       <div className="text-sm font-medium text-gray-900">
//                         ₦{item.price?.toLocaleString()}
//                       </div>
//                     </td>
//                     <td className="px-6 py-4 whitespace-nowrap">
//                       <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadge(item.status)}`}>
//                         {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
//                       </span>
//                     </td>
//                     <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
//                       {new Date(item.createdAt).toLocaleDateString()}
//                     </td>
//                     <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
//                       <div className="flex space-x-2">
//                         <button onClick={() => handleEditItem(item)} className="text-blue-600 hover:text-blue-900">
//                           <FiEdit3 className="w-4 h-4" />
//                         </button>
//                         <button onClick={() => handleDeleteItem(item._id)} className="text-red-600 hover:text-red-900">
//                           {deletingItemId === item._id ? <FiLoader className="animate-spin w-4 h-4" /> : <FiTrash2 className="w-4 h-4" />}
//                         </button>
//                       </div>
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//         ) : (
//           <div className="text-center py-8">
//             <FiPackage className="w-12 h-12 text-gray-400 mx-auto mb-4" />
//             <p className="text-gray-600">No items to manage</p>
//           </div>
//         )}
//       </div>

//       {/* Edit Item Modal */}
//       {editingItem && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
//           <div className="bg-white rounded-xl max-w-md w-full p-6">
//             <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Item</h3>
//             <form onSubmit={handleUpdateItem} className="space-y-4">
//               <input
//                 value={editData.name}
//                 onChange={e => setEditData(prev => ({ ...prev, name: e.target.value }))}
//                 className="input-field"
//                 placeholder="Name"
//               />
//               <textarea
//                 value={editData.description}
//                 onChange={e => setEditData(prev => ({ ...prev, description: e.target.value }))}
//                 className="input-field"
//                 rows={3}
//               />
//               <input
//                 type="number"
//                 value={editData.price ?? ''}
//                 onChange={e => setEditData(prev => ({ ...prev, price: Number(e.target.value) }))}
//                 className="input-field"
//                 placeholder="Price"
//               />
//               <select
//                 value={editData.status}
//                 onChange={e => setEditData(prev => ({ ...prev, status: e.target.value }))}
//                 className="input-field"
//               >
//                 <option value="paid">Paid</option>
//                 <option value="unpaid">Unpaid</option>
//                 <option value="pending">Pending</option>
//               </select>
//               <input
//                 value={editData.category}
//                 onChange={e => setEditData(prev => ({ ...prev, category: e.target.value }))}
//                 className="input-field"
//                 placeholder="Category"
//               />
//               <div className="flex justify-end space-x-3 pt-4 border-t">
//                 <button type="button" onClick={() => setEditingItem(null)} className="btn btn-secondary">Cancel</button>
//                 <button type="submit" className="btn btn-primary">
//                   {updatingItem ? <FiLoader className="animate-spin w-5 h-5" /> : 'Update'}
//                 </button>
//               </div>
//             </form>
//           </div>
//         </div>
//       )}
//     </div>
//   )
// }


// import { useState, useEffect } from 'react'
// import { useNavigate } from 'react-router-dom'
// import {
//   FiLogOut,
//   FiPackage,
//   FiCreditCard,
//   FiClock,
//   FiXCircle,
//   FiEdit3,
//   FiTrash2,
//   FiLoader
// } from 'react-icons/fi'
// import axios from 'axios'
// import toast from 'react-hot-toast'

// export default function UserAdminDashboard() {
//   const navigate = useNavigate()
//   const [stats, setStats] = useState({})
//   const [items, setItems] = useState([])
//   const [loading, setLoading] = useState(true)
//   const [editingItem, setEditingItem] = useState(null)
//   const [editData, setEditData] = useState({})
//   const [updatingItem, setUpdatingItem] = useState(false)
//   const [deletingItemId, setDeletingItemId] = useState(null)

//   useEffect(() => {
//     fetchDashboardData()
//   }, [])

//   const fetchDashboardData = async () => {
//     try {
//       const [dashboardResponse, itemsResponse] = await Promise.all([
//         axios.get('/admin/dashboard'),
//         axios.get('/admin/items?limit=20')
//       ])
//       setStats(dashboardResponse.data.stats)
//       setItems(itemsResponse.data.items)
//     } catch (error) {
//       toast.error('Error loading dashboard data')
//     } finally {
//       setLoading(false)
//     }
//   }

//   const handleLogoutAdmin = () => {
//     localStorage.removeItem('userAdminToken')
//     toast.success('Logged out from admin panel')
//     navigate('/dashboard')
//   }

//   const handleEditItem = (item) => {
//     setEditingItem(item._id)
//     setEditData({
//       name: item.name,
//       description: item.description,
//       price: item.price,
//       status: item.status,
//       category: item.category
//     })
//   }

//   const handleUpdateItem = async (e) => {
//     e.preventDefault()
//     setUpdatingItem(true)
//     try {
//       await axios.put(`/admin/items/${editingItem}`, editData)
//       toast.success('Item updated successfully')
//       setEditingItem(null)
//       fetchDashboardData()
//     } catch (error) {
//       toast.error(error.response?.data?.message || 'Error updating item')
//     } finally {
//       setUpdatingItem(false)
//     }
//   }

//   const handleDeleteItem = async (itemId) => {
//     if (!confirm('Are you sure you want to delete this item?')) return
//     setDeletingItemId(itemId)
//     try {
//       await axios.delete(`/admin/items/${itemId}`)
//       toast.success('Item deleted successfully')
//       fetchDashboardData()
//     } catch (error) {
//       toast.error(error.response?.data?.message || 'Error deleting item')
//     } finally {
//       setDeletingItemId(null)
//     }
//   }

//   const getStatusBadge = (status) => {
//     const badges = {
//       paid: 'bg-success-100 text-success-800',
//       unpaid: 'bg-error-100 text-error-800',
//       pending: 'bg-warning-100 text-warning-800'
//     }
//     return badges[status] || 'bg-gray-100 text-gray-800'
//   }

//   const statCards = [
//     {
//       title: 'Total Items',
//       value: stats.totalItems || 0,
//       icon: FiPackage,
//       color: 'bg-blue-500',
//       textColor: 'text-blue-600'
//     },
//     {
//       title: 'Paid Items',
//       value: (stats.paidItems || 0).toLocaleString(),
//       icon: FiCreditCard,
//       color: 'bg-success-500',
//       textColor: 'text-success-600'
//     },
//     {
//       title: 'Unpaid Items',
//       value: stats.unpaidItems || 0,
//       icon: FiXCircle,
//       color: 'bg-error-500',
//       textColor: 'text-error-600'
//     },
//     {
//       title: 'Pending Items',
//       value: stats.pendingItems || 0,
//       icon: FiClock,
//       color: 'bg-warning-500',
//       textColor: 'text-warning-600'
//     }
//   ]

//   if (loading) {
//     return (
//       <div className="animate-pulse">
//         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
//           {[...Array(4)].map((_, i) => (
//             <div key={i} className="card h-24 bg-gray-200"></div>
//           ))}
//         </div>
//       </div>
//     )
//   }

//   return (
//     <div className="max-w-7xl mx-auto">
//       {/* ... Header and Stat Cards ... */}

//       {/* Items Management */}
//       <div className="card">
//         <div className="flex items-center justify-between mb-6">
//           <h3 className="text-lg font-semibold text-gray-900">Items Management</h3>
//           <span className="text-sm text-gray-500">{items.length} items</span>
//         </div>

//         {items.length > 0 ? (
//           <div className="overflow-x-auto">
//             <table className="min-w-full divide-y divide-gray-200">
//               <thead className="bg-gray-50">
//                 <tr>
//                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
//                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
//                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
//                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
//                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
//                 </tr>
//               </thead>
//               <tbody className="bg-white divide-y divide-gray-200">
//                 {items.map((item) => (
//                   <tr key={item._id} className="hover:bg-gray-50">
//                     <td className="px-6 py-4 whitespace-nowrap">
//                       <div className="flex items-center">
//                         {item.image && (
//                           <img
//                             src={item.image}
//                             alt={item.name}
//                             className="w-10 h-10 rounded-lg object-cover mr-3"
//                           />
//                         )}
//                         <div>
//                           <div className="text-sm font-medium text-gray-900">{item.name}</div>
//                           {item.category && <div className="text-sm text-gray-500">{item.category}</div>}
//                         </div>
//                       </div>
//                     </td>
//                     <td className="px-6 py-4 whitespace-nowrap">
//                       <div className="text-sm font-medium text-gray-900">
//                         ₦{item.price?.toLocaleString()}
//                       </div>
//                     </td>
//                     <td className="px-6 py-4 whitespace-nowrap">
//                       <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadge(item.status)}`}>
//                         {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
//                       </span>
//                     </td>
//                     <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
//                       {new Date(item.createdAt).toLocaleDateString()}
//                     </td>
//                     <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
//                       <div className="flex space-x-2">
//                         <button onClick={() => handleEditItem(item)} className="text-blue-600 hover:text-blue-900">
//                           <FiEdit3 className="w-4 h-4" />
//                         </button>
//                         <button onClick={() => handleDeleteItem(item._id)} className="text-red-600 hover:text-red-900">
//                           {deletingItemId === item._id ? <FiLoader className="animate-spin w-4 h-4" /> : <FiTrash2 className="w-4 h-4" />}
//                         </button>
//                       </div>
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//         ) : (
//           <div className="text-center py-8">
//             <FiPackage className="w-12 h-12 text-gray-400 mx-auto mb-4" />
//             <p className="text-gray-600">No items to manage</p>
//           </div>
//         )}
//       </div>

//       {/* Edit Item Modal */}
//       {editingItem && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
//           <div className="bg-white rounded-xl max-w-md w-full p-6">
//             <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Item</h3>
//             <form onSubmit={handleUpdateItem} className="space-y-4">
//               <input value={editData.name} onChange={e => setEditData(prev => ({ ...prev, name: e.target.value }))} className="input-field" placeholder="Name" />
//               <textarea value={editData.description} onChange={e => setEditData(prev => ({ ...prev, description: e.target.value }))} className="input-field" rows={3} />
//               <input type="number" value={editData.price} onChange={e => setEditData(prev => ({ ...prev, price: Number(e.target.value) }))} className="input-field" placeholder="Price" />
//               <select value={editData.status} onChange={e => setEditData(prev => ({ ...prev, status: e.target.value }))} className="input-field">
//                 <option value="paid">Paid</option>
//                 <option value="unpaid">Unpaid</option>
//                 <option value="pending">Pending</option>
//               </select>
//               <input value={editData.category} onChange={e => setEditData(prev => ({ ...prev, category: e.target.value }))} className="input-field" placeholder="Category" />
//               <div className="flex justify-end space-x-3 pt-4 border-t">
//                 <button type="button" onClick={() => setEditingItem(null)} className="btn btn-secondary">Cancel</button>
//                 <button type="submit" className="btn btn-primary">
//                   {updatingItem ? <FiLoader className="animate-spin w-5 h-5" /> : 'Update'}
//                 </button>
//               </div>
//             </form>
//           </div>
//         </div>
//       )}
//     </div>
//   )
// }
